import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import AdminSidebar from "../components/AdminSidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  TrendingUp, Bell, Beaker, Mail, Send, Plus, X, Play, Pause,
  CheckCircle, Target, Users, Eye, MousePointer, Trash2, ChevronDown, ChevronUp
} from "lucide-react";

const AdminGrowth = () => {
  const [activeSection, setActiveSection] = useState("alerts");
  const [alerts, setAlerts] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [showNewExperiment, setShowNewExperiment] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [expForm, setExpForm] = useState({ name: "", description: "", target_tier: "recruiter_starter", traffic_split: 50, variant_a: { price: "" }, variant_b: { price: "" } });
  const [campForm, setCampForm] = useState({ name: "", subject: "", body_html: "", target_audience: "all" });
  const [alertEmail, setAlertEmail] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, e, c] = await Promise.all([
        fetch(`${API}/admin/growth/revenue-alerts`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/admin/growth/experiments`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/admin/growth/campaigns`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
      ]);
      setAlerts(a);
      setExperiments(e);
      setCampaigns(c);
      if (a?.alert_email) setAlertEmail(a.alert_email);
    } catch {} finally { setLoading(false); }
  };

  const updateAlertSettings = async () => {
    const res = await fetch(`${API}/admin/growth/revenue-alerts/settings`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: true, email: alertEmail }),
    });
    if (res.ok) toast.success("Alert settings saved");
  };

  const createExperiment = async () => {
    const res = await fetch(`${API}/admin/growth/experiments`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(expForm),
    });
    if (res.ok) {
      toast.success("Experiment created");
      setShowNewExperiment(false);
      setExpForm({ name: "", description: "", target_tier: "recruiter_starter", traffic_split: 50, variant_a: { price: "" }, variant_b: { price: "" } });
      fetchAll();
    }
  };

  const updateExpStatus = async (id, status) => {
    const res = await fetch(`${API}/admin/growth/experiments/${id}/status`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (res.ok) { toast.success(`Experiment ${status}`); fetchAll(); }
  };

  const createCampaign = async () => {
    const res = await fetch(`${API}/admin/growth/campaigns`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(campForm),
    });
    if (res.ok) {
      toast.success("Campaign created");
      setShowNewCampaign(false);
      setCampForm({ name: "", subject: "", body_html: "", target_audience: "all" });
      fetchAll();
    }
  };

  const sendCampaign = async (id) => {
    const res = await fetch(`${API}/admin/growth/campaigns/${id}/send`, {
      method: "POST", credentials: "include",
    });
    if (res.ok) {
      const data = await res.json();
      toast.success(`Campaign sent to ${data.recipients} recipients`);
      fetchAll();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Failed to send");
    }
  };

  const deleteCampaign = async (id) => {
    const res = await fetch(`${API}/admin/growth/campaigns/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Deleted"); fetchAll(); }
  };

  const inputClass = "w-full bg-obsidian border border-[#333] rounded px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none";

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <AdminSidebar />
        <main className="lg:ml-64 p-6 lg:p-8 pt-20 lg:pt-8 flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-growth-page">
      <AdminSidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold">
            GROWTH <span className="text-crimson">TOOLS</span>
          </h1>
          <p className="text-gray-400 text-sm mt-1">Revenue alerts, pricing experiments, and email campaigns</p>
        </motion.div>

        {/* Section Tabs */}
        <div className="flex gap-2 mb-8">
          {[
            { key: "alerts", icon: Bell, label: "Revenue Alerts" },
            { key: "experiments", icon: Beaker, label: "A/B Tests" },
            { key: "campaigns", icon: Mail, label: "Campaigns" },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveSection(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                activeSection === tab.key ? "bg-crimson text-white" : "bg-[#111] border border-[#222] text-gray-400 hover:text-white"
              }`}
              data-testid={`tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ Revenue Alerts ═══ */}
        {activeSection === "alerts" && alerts && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* MRR Progress */}
            <div className="bg-[#111] border border-[#222] p-6" data-testid="mrr-milestone-card">
              <h3 className="font-heading text-lg font-bold mb-1">MRR MILESTONE TRACKER</h3>
              <p className="text-xs text-gray-500 mb-4">Current Monthly Recurring Revenue progress</p>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-heading text-4xl font-bold text-crimson">${alerts.current_mrr.toLocaleString()}</span>
                <span className="text-gray-500 text-sm">/ ${alerts.next_milestone?.toLocaleString() || "Max"} next goal</span>
              </div>
              <div className="h-4 bg-[#1a1a1a] overflow-hidden mb-3">
                <motion.div
                  className="h-full bg-gradient-to-r from-crimson to-emerald-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(alerts.progress_pct, 100)}%` }}
                  transition={{ duration: 1 }}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {alerts.milestones.map(m => (
                  <div key={m} className={`text-xs px-2.5 py-1 border ${
                    alerts.achieved_milestones?.includes(m)
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                      : m === alerts.next_milestone
                        ? "border-crimson bg-crimson/10 text-crimson"
                        : "border-[#222] text-gray-600"
                  }`}>
                    ${m.toLocaleString()}
                    {alerts.achieved_milestones?.includes(m) && <CheckCircle className="w-3 h-3 inline ml-1" />}
                  </div>
                ))}
              </div>
            </div>
            {/* Alert Settings */}
            <div className="bg-[#111] border border-[#222] p-6" data-testid="alert-settings">
              <h3 className="font-heading text-lg font-bold mb-4">Alert Settings</h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-xs text-gray-400 mb-1 block">Notification Email</label>
                  <input type="email" value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} placeholder="admin@rekrutor.com" className={inputClass} data-testid="alert-email-input" />
                </div>
                <Button onClick={updateAlertSettings} className="bg-crimson hover:bg-crimson/90 text-white" data-testid="save-alerts-btn">Save</Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ═══ A/B Experiments ═══ */}
        {activeSection === "experiments" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading text-lg font-bold">Pricing Experiments</h3>
              <Button onClick={() => setShowNewExperiment(true)} className="bg-crimson hover:bg-crimson/90 text-white text-sm" data-testid="new-experiment-btn">
                <Plus className="w-4 h-4 mr-1" /> New Experiment
              </Button>
            </div>

            {/* New Experiment Form */}
            <AnimatePresence>
              {showNewExperiment && (
                <motion.div className="bg-[#111] border border-[#222] p-6" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-heading text-sm font-bold">Create A/B Test</h4>
                    <button onClick={() => setShowNewExperiment(false)}><X className="w-4 h-4 text-gray-500" /></button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Experiment Name</label>
                      <input value={expForm.name} onChange={(e) => setExpForm({ ...expForm, name: e.target.value })} className={inputClass} placeholder="e.g., Starter Price Test" data-testid="exp-name" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Target Tier</label>
                      <select value={expForm.target_tier} onChange={(e) => setExpForm({ ...expForm, target_tier: e.target.value })} className={inputClass} data-testid="exp-tier">
                        <option value="recruiter_starter">Recruiter Starter</option>
                        <option value="recruiter_pro">Recruiter Pro</option>
                        <option value="jobseeker_plus">Jobseeker Plus</option>
                        <option value="jobseeker_pro">Jobseeker Pro</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Variant A Price</label>
                      <input type="number" value={expForm.variant_a.price} onChange={(e) => setExpForm({ ...expForm, variant_a: { price: e.target.value } })} className={inputClass} placeholder="29" data-testid="exp-price-a" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Variant B Price</label>
                      <input type="number" value={expForm.variant_b.price} onChange={(e) => setExpForm({ ...expForm, variant_b: { price: e.target.value } })} className={inputClass} placeholder="25" data-testid="exp-price-b" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-xs text-gray-400 mb-1 block">Traffic Split (% to Variant B)</label>
                    <input type="range" min={10} max={90} value={expForm.traffic_split} onChange={(e) => setExpForm({ ...expForm, traffic_split: parseInt(e.target.value) })} className="w-full" />
                    <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                      <span>A: {100 - expForm.traffic_split}%</span>
                      <span>B: {expForm.traffic_split}%</span>
                    </div>
                  </div>
                  <Button onClick={createExperiment} className="bg-crimson hover:bg-crimson/90 text-white" disabled={!expForm.name} data-testid="create-exp-btn">Create Experiment</Button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Experiment List */}
            {experiments.map(exp => (
              <div key={exp.experiment_id} className="bg-[#111] border border-[#222] p-5" data-testid={`exp-${exp.experiment_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-sm">{exp.name}</h4>
                    <p className="text-[10px] text-gray-500">{exp.target_tier} — {exp.traffic_split}% to B</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 ${
                      exp.status === "active" ? "bg-emerald-500/20 text-emerald-400" :
                      exp.status === "paused" ? "bg-amber-500/20 text-amber-400" :
                      exp.status === "completed" ? "bg-blue-500/20 text-blue-400" :
                      "bg-gray-500/20 text-gray-400"
                    }`}>{exp.status}</span>
                    {exp.status === "draft" && (
                      <button onClick={() => updateExpStatus(exp.experiment_id, "active")} className="text-emerald-400 hover:text-emerald-300" title="Start">
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    {exp.status === "active" && (
                      <button onClick={() => updateExpStatus(exp.experiment_id, "paused")} className="text-amber-400 hover:text-amber-300" title="Pause">
                        <Pause className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#0a0a0a] border border-[#1a1a1a]">
                    <p className="text-[10px] text-gray-500">Variant A</p>
                    <p className="text-sm font-bold">${exp.variant_a?.price || "Current"}</p>
                    <p className="text-[10px] text-gray-500">{exp.results?.variant_a?.views || 0} views — {exp.results?.variant_a?.conversions || 0} conversions</p>
                  </div>
                  <div className="p-3 bg-[#0a0a0a] border border-[#1a1a1a]">
                    <p className="text-[10px] text-gray-500">Variant B</p>
                    <p className="text-sm font-bold">${exp.variant_b?.price || "Test"}</p>
                    <p className="text-[10px] text-gray-500">{exp.results?.variant_b?.views || 0} views — {exp.results?.variant_b?.conversions || 0} conversions</p>
                  </div>
                </div>
              </div>
            ))}
            {experiments.length === 0 && !showNewExperiment && (
              <div className="text-center py-12 bg-[#0a0a0a] border border-[#1a1a1a] text-gray-500 text-sm">
                No experiments yet. Create one to start testing pricing variants.
              </div>
            )}
          </motion.div>
        )}

        {/* ═══ Bulk Email Campaigns ═══ */}
        {activeSection === "campaigns" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading text-lg font-bold">Email Campaigns</h3>
              <Button onClick={() => setShowNewCampaign(true)} className="bg-crimson hover:bg-crimson/90 text-white text-sm" data-testid="new-campaign-btn">
                <Plus className="w-4 h-4 mr-1" /> New Campaign
              </Button>
            </div>

            {/* New Campaign Form */}
            <AnimatePresence>
              {showNewCampaign && (
                <motion.div className="bg-[#111] border border-[#222] p-6" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-heading text-sm font-bold">Create Campaign</h4>
                    <button onClick={() => setShowNewCampaign(false)}><X className="w-4 h-4 text-gray-500" /></button>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Campaign Name</label>
                      <input value={campForm.name} onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} className={inputClass} placeholder="e.g., Winter Promo" data-testid="camp-name" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Target Audience</label>
                      <select value={campForm.target_audience} onChange={(e) => setCampForm({ ...campForm, target_audience: e.target.value })} className={inputClass} data-testid="camp-audience">
                        <option value="all">All Users</option>
                        <option value="recruiters">Recruiters Only</option>
                        <option value="jobseekers">Job Seekers Only</option>
                        <option value="free_users">Free Users Only</option>
                        <option value="paid_users">Paid Users Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Email Subject</label>
                      <input value={campForm.subject} onChange={(e) => setCampForm({ ...campForm, subject: e.target.value })} className={inputClass} placeholder="Your hiring game just got better" data-testid="camp-subject" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Email Body (HTML)</label>
                      <textarea value={campForm.body_html} onChange={(e) => setCampForm({ ...campForm, body_html: e.target.value })} className={`${inputClass} min-h-[120px] resize-y`} placeholder="<h1>Hello!</h1><p>Your email content...</p>" data-testid="camp-body" />
                    </div>
                  </div>
                  <Button onClick={createCampaign} className="bg-crimson hover:bg-crimson/90 text-white" disabled={!campForm.name || !campForm.subject} data-testid="create-camp-btn">Create Campaign</Button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Campaign List */}
            {campaigns.map(camp => (
              <div key={camp.campaign_id} className="bg-[#111] border border-[#222] p-5" data-testid={`camp-${camp.campaign_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-sm">{camp.name}</h4>
                    <p className="text-[10px] text-gray-500">{camp.subject} — {camp.target_audience}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 ${
                      camp.status === "sent" ? "bg-emerald-500/20 text-emerald-400" :
                      "bg-gray-500/20 text-gray-400"
                    }`}>{camp.status}</span>
                    {camp.status === "draft" && (
                      <>
                        <button onClick={() => sendCampaign(camp.campaign_id)} className="text-emerald-400 hover:text-emerald-300" title="Send" data-testid={`send-camp-${camp.campaign_id}`}>
                          <Send className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteCampaign(camp.campaign_id)} className="text-red-400 hover:text-red-300" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {camp.status === "sent" && (
                  <div className="grid grid-cols-4 gap-2">
                    <div className="p-2 bg-[#0a0a0a] border border-[#1a1a1a] text-center">
                      <Users className="w-3 h-3 text-gray-500 mx-auto mb-1" />
                      <p className="text-sm font-bold">{camp.stats?.sent || 0}</p>
                      <p className="text-[9px] text-gray-600">Sent</p>
                    </div>
                    <div className="p-2 bg-[#0a0a0a] border border-[#1a1a1a] text-center">
                      <Eye className="w-3 h-3 text-blue-400 mx-auto mb-1" />
                      <p className="text-sm font-bold">{camp.stats?.opened || 0}</p>
                      <p className="text-[9px] text-gray-600">Opened</p>
                    </div>
                    <div className="p-2 bg-[#0a0a0a] border border-[#1a1a1a] text-center">
                      <MousePointer className="w-3 h-3 text-emerald-400 mx-auto mb-1" />
                      <p className="text-sm font-bold">{camp.stats?.clicked || 0}</p>
                      <p className="text-[9px] text-gray-600">Clicked</p>
                    </div>
                    <div className="p-2 bg-[#0a0a0a] border border-[#1a1a1a] text-center">
                      <Target className="w-3 h-3 text-crimson mx-auto mb-1" />
                      <p className="text-sm font-bold">{camp.stats?.open_rate || 0}%</p>
                      <p className="text-[9px] text-gray-600">Open Rate</p>
                    </div>
                  </div>
                )}
                {camp.status === "draft" && (
                  <p className="text-xs text-gray-500">Audience: ~{camp.audience_count} users</p>
                )}
              </div>
            ))}
            {campaigns.length === 0 && !showNewCampaign && (
              <div className="text-center py-12 bg-[#0a0a0a] border border-[#1a1a1a] text-gray-500 text-sm">
                No campaigns yet. Create your first email campaign.
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AdminGrowth;
